import React from "react";
import { motion } from "framer-motion";

interface NetworkCubeProps {
  isHovered: boolean;
  borderColor?: string;
  nodeColor?: string;
  content?: React.ReactNode;
  isAnimating?: boolean;
}

export default function NetworkCube({
  isHovered,
  borderColor = "white",
  nodeColor = "#7fff00",
  content,
  isAnimating = false,
}: NetworkCubeProps) {
  // Animation variants for the nodes
  const nodeVariants = {
    visible: { opacity: 1, scale: 1 },
    hidden: { opacity: 0, scale: 0 },
  };

  // Animation variants for the lines
  const lineVariants = {
    visible: { opacity: 1, pathLength: 1 },
    hidden: { opacity: 0, pathLength: 0 },
  };

  // Determine if we should show the content based on hover or animation
  const shouldShowContent = isHovered || isAnimating;

  return (
    <g>
      {/* Cube outline - always visible */}
      <path
        d="M -80 -50 L -80 50 L 0 100 L 80 50 L 80 -50 L 0 -100 Z"
        fill="#1a1a1a"
        stroke={borderColor}
        strokeWidth="3"
      />

      {/* Text that appears on hover or during animation */}
      {shouldShowContent && (
        <motion.foreignObject
          x="-60"
          y="-40"
          width="120"
          height="80"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
        >
          {content}
        </motion.foreignObject>
      )}

      {/* Network nodes and connections - disappear on hover or during animation */}
      <motion.g
        initial="visible"
        animate={shouldShowContent ? "hidden" : "visible"}
        variants={{ visible: { opacity: 1 }, hidden: { opacity: 0 } }}
        transition={{ duration: 0.5 }}
      >
        {/* Center node */}
        <motion.circle
          cx="0"
          cy="0"
          r="12"
          fill={nodeColor}
          variants={nodeVariants}
        />

        {/* Top node */}
        <motion.circle
          cx="0"
          cy="-60"
          r="12"
          fill={nodeColor}
          variants={nodeVariants}
        />
        <motion.line
          x1="0"
          y1="-48"
          x2="0"
          y2="-12"
          stroke={nodeColor}
          strokeWidth="3"
          variants={lineVariants}
        />

        {/* Bottom node */}
        <motion.circle
          cx="0"
          cy="60"
          r="12"
          fill={nodeColor}
          variants={nodeVariants}
        />
        <motion.line
          x1="0"
          y1="48"
          x2="0"
          y2="12"
          stroke={nodeColor}
          strokeWidth="3"
          variants={lineVariants}
        />

        {/* Left node */}
        <motion.circle
          cx="-50"
          cy="0"
          r="12"
          fill={nodeColor}
          variants={nodeVariants}
        />
        <motion.line
          x1="-38"
          y1="0"
          x2="-12"
          y2="0"
          stroke={nodeColor}
          strokeWidth="3"
          variants={lineVariants}
        />

        {/* Right node */}
        <motion.circle
          cx="50"
          cy="0"
          r="12"
          fill={nodeColor}
          variants={nodeVariants}
        />
        <motion.line
          x1="38"
          y1="0"
          x2="12"
          y2="0"
          stroke={nodeColor}
          strokeWidth="3"
          variants={lineVariants}
        />

        {/* Top-left node */}
        <motion.circle
          cx="-40"
          cy="-40"
          r="12"
          fill={nodeColor}
          variants={nodeVariants}
        />
        <motion.line
          x1="-30"
          y1="-30"
          x2="-10"
          y2="-10"
          stroke={nodeColor}
          strokeWidth="3"
          variants={lineVariants}
        />
        <motion.line
          x1="-40"
          y1="-28"
          x2="-40"
          y2="-12"
          stroke={nodeColor}
          strokeWidth="3"
          opacity="0.7"
          variants={lineVariants}
        />
        <motion.line
          x1="-28"
          y1="-40"
          x2="-12"
          y2="-40"
          stroke={nodeColor}
          strokeWidth="3"
          opacity="0.7"
          variants={lineVariants}
        />

        {/* Top-right node */}
        <motion.circle
          cx="40"
          cy="-40"
          r="12"
          fill={nodeColor}
          variants={nodeVariants}
        />
        <motion.line
          x1="30"
          y1="-30"
          x2="10"
          y2="-10"
          stroke={nodeColor}
          strokeWidth="3"
          variants={lineVariants}
        />
        <motion.line
          x1="40"
          y1="-28"
          x2="40"
          y2="-12"
          stroke={nodeColor}
          strokeWidth="3"
          opacity="0.7"
          variants={lineVariants}
        />
        <motion.line
          x1="28"
          y1="-40"
          x2="12"
          y2="-40"
          stroke={nodeColor}
          strokeWidth="3"
          opacity="0.7"
          variants={lineVariants}
        />

        {/* Bottom-left node */}
        <motion.circle
          cx="-40"
          cy="40"
          r="12"
          fill={nodeColor}
          variants={nodeVariants}
        />
        <motion.line
          x1="-30"
          y1="30"
          x2="-10"
          y2="10"
          stroke={nodeColor}
          strokeWidth="3"
          variants={lineVariants}
        />
        <motion.line
          x1="-40"
          y1="28"
          x2="-40"
          y2="12"
          stroke={nodeColor}
          strokeWidth="3"
          opacity="0.7"
          variants={lineVariants}
        />
        <motion.line
          x1="-28"
          y1="40"
          x2="-12"
          y2="40"
          stroke={nodeColor}
          strokeWidth="3"
          opacity="0.7"
          variants={lineVariants}
        />

        {/* Bottom-right node */}
        <motion.circle
          cx="40"
          cy="40"
          r="12"
          fill={nodeColor}
          variants={nodeVariants}
        />
        <motion.line
          x1="30"
          y1="30"
          x2="10"
          y2="10"
          stroke={nodeColor}
          strokeWidth="3"
          variants={lineVariants}
        />
        <motion.line
          x1="40"
          y1="28"
          x2="40"
          y2="12"
          stroke={nodeColor}
          strokeWidth="3"
          opacity="0.7"
          variants={lineVariants}
        />
        <motion.line
          x1="28"
          y1="40"
          x2="12"
          y2="40"
          stroke={nodeColor}
          strokeWidth="3"
          opacity="0.7"
          variants={lineVariants}
        />

        {/* Cross connections */}
        <motion.line
          x1="-40"
          y1="-40"
          x2="40"
          y2="40"
          stroke={nodeColor}
          strokeWidth="2"
          opacity="0.5"
          variants={lineVariants}
        />
        <motion.line
          x1="40"
          y1="-40"
          x2="-40"
          y2="40"
          stroke={nodeColor}
          strokeWidth="2"
          opacity="0.5"
          variants={lineVariants}
        />
        <motion.line
          x1="0"
          y1="-60"
          x2="0"
          y2="60"
          stroke={nodeColor}
          strokeWidth="2"
          opacity="0.5"
          variants={lineVariants}
        />
        <motion.line
          x1="-50"
          y1="0"
          x2="50"
          y2="0"
          stroke={nodeColor}
          strokeWidth="2"
          opacity="0.5"
          variants={lineVariants}
        />
      </motion.g>
    </g>
  );
}
